import { ComponentProps, useEffect, useState } from 'react';
import {
    Box, IconButton, InputAdornment, TextField,
} from '@mui/material';
import { keyframes } from '@emotion/react';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

// flash color animation for stepper button presses
const flashKeyframes = keyframes`
    from {
        background-color: #fff59d;
    }
    to {
        background-color: transparent;
    }
`;

interface NumericInputProps extends Omit<ComponentProps<typeof TextField>, 'value' | 'onChange' | 'onBlur' | 'onKeyDown' | 'min' | 'max' | 'step' > {
    value: number | undefined;
    onChange?: (value: number | undefined) => void;
    allowDecimals?: boolean;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    helperText?: string;
    showStepper?: boolean;
    onBlur?: (value: number | undefined, event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>, value: number | undefined) => void;
}

export const NumericInput: React.FC<NumericInputProps> = ({
    label,
    value,
    onChange,
    allowDecimals = false,
    min,
    max,
    placeholder,
    step,
    required = false,
    disabled = false,
    helperText,
    showStepper = false,
    onBlur,
    onKeyDown,
}) => {
    const [inputValue, setInputValue] = useState<string>(value?.toString() || '');
    const [error, setError] = useState<string | null>(null);
    const [upFlashSeq, setUpFlashSeq] = useState(0);
    const [downFlashSeq, setDownFlashSeq] = useState(0);

    useEffect(() => {
        setInputValue(value === undefined || Number.isNaN(value) ? '' : String(value));
    }, [value]);

    const isStepValid = (num: number, stepValue: number): boolean => {
        const tolerance = 1e-10;

        return Math.abs(num % stepValue) < tolerance || Math.abs(num % stepValue - stepValue) < tolerance;
    };

    const parseCurrent = (raw: string): { parsed: number | undefined; error: string | null } => {
        if (raw === '') {
            return required ? { parsed: undefined, error: 'This field is required' } : { parsed: undefined, error: null };
        }
        if (allowDecimals && raw === '.') {
            return { parsed: undefined, error: null };
        }
        const parsedValue = allowDecimals ? parseFloat(raw) : parseInt(raw, 10);

        if (Number.isNaN(parsedValue)) {
            return { parsed: undefined, error: 'Invalid number' };
        }
        if (min !== undefined && parsedValue < min) {
            return { parsed: undefined, error: `Value must be at least ${min}` };
        }
        if (max !== undefined && parsedValue > max) {
            return { parsed: undefined, error: `Value must be at most ${max}` };
        }
        if (step !== undefined && !isStepValid(parsedValue, step)) {
            return { parsed: undefined, error: `Value must be a multiple of ${step}` };
        }
        return { parsed: parsedValue, error: null };
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const input = e.target.value;

        setInputValue(input);

        const { parsed, error: nextError } = parseCurrent(input);

        setError(nextError);
        if (onChange) {
            onChange(parsed);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        const { parsed, error: nextError } = parseCurrent(inputValue);

        setError(nextError);
        if (onBlur) {
            onBlur(parsed, e);
        }
    };

    const stepAmount = step ?? 1;
    const canStepUp = !disabled && (max === undefined || (value ?? min ?? 0) < max);
    const canStepDown = !disabled && (min === undefined || (value ?? min ?? 0) > min);

    const commitStep = (direction: 1 | -1): void => {
        let next = (value ?? min ?? 0) + direction * stepAmount;

        if (min !== undefined) next = Math.max(next, min);
        if (max !== undefined) next = Math.min(next, max);
        if (!allowDecimals) next = Math.round(next);

        setInputValue(String(next));
        setError(null);
        if (direction === 1) {
            setUpFlashSeq((prev) => prev + 1);
        } else {
            setDownFlashSeq((prev) => prev + 1);
        }
        if (onChange) {
            onChange(next);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (showStepper && !disabled) {
            if (e.key === 'ArrowUp' && canStepUp) {
                e.preventDefault();
                commitStep(1);
            } else if (e.key === 'ArrowDown' && canStepDown) {
                e.preventDefault();
                commitStep(-1);
            }
        }

        const { parsed } = parseCurrent(inputValue);

        if (onKeyDown) {
            onKeyDown(e, parsed);
        }
    };

    return (
        <TextField
            disabled={disabled}
            error={!!error}
            helperText={error ?? helperText}
            InputProps={showStepper ? {
                endAdornment: (
                    <InputAdornment position="end">
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <IconButton
                                disableRipple
                                aria-label="Increase value"
                                disabled={!canStepUp}
                                size="small"
                                sx={{ p: 0, height: 16, position: 'relative' }}
                                tabIndex={-1}
                                onClick={(): void => commitStep(1)}
                            >
                                <KeyboardArrowUp fontSize="inherit" />
                                {upFlashSeq > 0 && (
                                    <Box
                                        key={upFlashSeq}
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            borderRadius: '4px',
                                            pointerEvents: 'none',
                                            animation: `${flashKeyframes} 500ms ease-out`,
                                        }}
                                    />
                                )}
                            </IconButton>
                            <IconButton
                                disableRipple
                                aria-label="Decrease value"
                                disabled={!canStepDown}
                                size="small"
                                sx={{ p: 0, height: 16, position: 'relative' }}
                                tabIndex={-1}
                                onClick={(): void => commitStep(-1)}
                            >
                                <KeyboardArrowDown fontSize="inherit" />
                                {downFlashSeq > 0 && (
                                    <Box
                                        key={downFlashSeq}
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            borderRadius: '4px',
                                            pointerEvents: 'none',
                                            animation: `${flashKeyframes} 500ms ease-out`,
                                        }}
                                    />
                                )}
                            </IconButton>
                        </Box>
                    </InputAdornment>
                ),
            } : undefined}
            label={label}
            placeholder={placeholder}
            required={required}
            size="small"
            value={inputValue}
            onBlur={handleBlur}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
        />
    );
};
